// Deals (module 10 U203 to U221, U278 to U283; the briefs DEALS-PAGE-BRIEF-2026-10-03.md section 5 and
// DEALS-ONE-LIST-BRIEF-2026-10-05.md; drawn in screens/2026-10-03-deals-concepts/concept-b-lines.html). Mike, 4 Oct 2026, asking for it (P4): "I think
// everything needs to be in one like with each card expandable on click. I think we also need to have a series of
// circular graphs showing revenue potential per product etc".
//
// Top to bottom: the head with the find; the show line; revenue potential by product, one ring each; what the deals
// wait on, our decisions and the clients' promises; the line of proposals; the filter bar with Everyone and Mine at its
// front; then one line per open opportunity, ranked by keenness for everyone (pipeline P8; Mike, 5 Oct 2026: "the
// keenest accounts are co-mingled regardless, not segmented"), each opening on a press into its card beside the
// account's products. The rings and the waiting lines add up the lines the filters leave (U280).
//
// U152 is the rule, as on the desk this replaces: every value comes from GET /api/deals/heat, and a control is drawn
// only where its machinery exists. Not drawn yet, for want of it: Add notes, Ask <name> for it, Draft a chase and Set a
// re-contact date. The yellow on a line is the reader's own step (U216), and opens its item, where its options and their
// reasons are.
//
// Pipeline P5 (DEALS-LIVE-CARD-BRIEF-2026-10-04.md, a-deals.html; module 10 U231 to U243) made it live: "Since this
// morning" under the title, the rings in one row, By stage, the line in two rows and each opportunity as a card that is
// the deal, with its keenness, figure, product and stage set on it (LiveCard.tsx, which the company record imports).
import { ActionTextarea, ActionSelect, ActionInput, ActionButton } from '../parts/index.js';
import { currentViewing } from '../view-as.js';
import { useEffect, useMemo, useState } from 'react';
import { dayMonth } from '../day.js';
import { AddedLine } from '../dealwork/AddedLine.js';
import { useFilterParams } from '../find/params.js';
import { Loading, useLoad, type PageProps } from '../records/shared.js';
import { AddNotes } from '../notes/AddNotes.js';
import { ShowStrip } from '../show/ShowStrip.js';
import { dealsApi, DealsError, type ByKeenness, type DealsList, type Found, type Heat, type HeatMoney, type HeatRow, type HoldDecided, type HoldLine, type PromiseLine, type ShowLine, type SortedByHand, type ToSort } from './api.js';
import { HeardCard } from './DealsPage.js';
import { dueWords, filterOptions, foundDealWhat, firstName, holdInView, holdWhoWords, lineInFilter, lineMatches, moneyFull, moneyOf, moneyShort, promiseInView, ringSegments, ringsInView, waitingWords, whoseRingWords, withFigureWords, type FilterOption, type HeatFilter } from './heat-words.js';
import { DealsLine, liveContextOf, OpenedLine, useCardNotes, type LiveContext } from './LiveCard.js';
import { type LineRank } from './live-words.js';
import './deals.css';
import { ClosedDeals } from './ClosedDeals.js';
import { CloseToasts } from './CloseBox.js';

const RING_TOKEN: Record<keyof ByKeenness, string> = {
  k5: 'var(--keen-5)', k4: 'var(--keen-4)', k3: 'var(--keen-3)', k2: 'var(--keen-2)', k1: 'var(--keen-1)', none: 'var(--ring-none)',
};

/** A ring: the track, and the keenness steps that hold figures over it, keenest first from the top. */
function Ring({ k, size, width }: { k: ByKeenness; size: number; width: number }) {
  const c = size / 2;
  const r = c - width / 2;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--ring-track)" strokeWidth={width} />
      {ringSegments(k, circ).map((s) => (
        <circle key={s.key} cx={c} cy={c} r={r} fill="none" stroke={RING_TOKEN[s.key]} strokeWidth={width}
          strokeDasharray={`${s.len} ${circ - s.len}`} strokeDashoffset={-s.offset} transform={`rotate(-90 ${c} ${c})`} />
      ))}
    </svg>
  );
}

function Chev() {
  return <svg className="hx-chev" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>;
}

/** Figures in a currency other than the store's own are never converted: they are said beside the total. */
function otherWords(m: HeatMoney): string | null {
  return m.other.length === 0 ? null : m.other.map((o) => moneyFull(o.amount, o.currency)).join(', ');
}

/** Where the show stands: still on, or after it. */
function showWhen(s: ShowLine, now: string): string {
  return now.slice(0, 10) > s.endsOn ? 'after the show' : 'on now';
}

/**
 * One decision Waiting on us holds. Anyone on the desk may record it (REQ-2026-10-07-171; Mike by pop-up, 7 Oct 2026:
 * "Decided button, tonight (Recommended)" and "Anyone on the desk (Recommended)"): Decided opens a box for what was
 * decided, and recording it closes the hold, so the line leaves and its deals stop counting as held. The button is drawn
 * only where the route would take the press (U152): for a reader who may change deals.
 */
function HoldRow({ h, home, held, goTo, store, may, onDecided }: {
  h: HoldLine; home: string | null; held: HeatRow[]; goTo: (dealId: string) => void;
  store: string; may: boolean; onDecided: (d: HoldDecided) => void;
}) {
  const [isOpen, setOpen] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [words, setWords] = useState('');
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);
  const proposed = h.proposed && h.proposed.withFigure > 0 ? moneyFull(h.proposed.home, home) : null;
  const record = () => {
    if (!words.trim()) { setWrong('Say what was decided.'); return; }
    setBusy(true); setWrong(null);
    dealsApi.decideHold(store, h.holdId, words)
      .then((d) => { setBusy(false); setDeciding(false); setWords(''); onDecided(d); })
      .catch((e: unknown) => { setBusy(false); setWrong(e instanceof DealsError ? `Not recorded: ${e.message}.` : 'Not recorded. Try again.'); });
  };
  return (
    <div className={isOpen || deciding ? 'hx-wrow open' : 'hx-wrow'}>
      <div className="hx-line hx-wl" onClick={() => setOpen(!isOpen)}>
        <span className="hx-mini"><Ring k={h.ring.byKeenness} size={28} width={6} /></span>
        <span>
          <ActionButton reading type="button" className="t" aria-expanded={isOpen} onClick={(e) => { e.stopPropagation(); setOpen(!isOpen); }}>{h.title}</ActionButton>
          <span className="s">
            <span><b>{h.holds}</b>{h.holds === 1 ? 'opportunity' : 'opportunities'}, <b>{h.keen}</b>keen</span>
            {proposed ? <span><b>{proposed}</b>proposed minimums</span> : null}
            <span className="who2">{holdWhoWords(h)}</span>
            {may && !deciding
              ? <ActionButton type="button" className="hx-link hx-decided" onClick={(e) => { e.stopPropagation(); setDeciding(true); setWrong(null); }}>Decided</ActionButton>
              : null}
          </span>
        </span>
        <Chev />
      </div>
      {deciding
        ? (
          <div className="hx-wbody">
            <div className="dw-box">
              <label className="dwn-l" htmlFor={`hx-dec-${h.holdId}`}>What was decided?</label>
              <ActionTextarea id={`hx-dec-${h.holdId}`} rows={2} maxLength={500} value={words} disabled={busy}
                placeholder="e.g. offers go out Mon 12 Oct" onChange={(e) => setWords(e.target.value)} />
              {wrong ? <p className="dwn-note warn" role="alert">{wrong}</p> : null}
              <div className="dl-opts">
                <ActionButton type="button" className="dl-opt" disabled={busy} onClick={record}>Record it</ActionButton>
                <ActionButton type="button" className="dl-opt" disabled={busy} onClick={() => { setDeciding(false); setWrong(null); }}>Cancel</ActionButton>
              </div>
            </div>
          </div>
        )
        : null}
      {isOpen
        ? (
          <div className="hx-wbody">
            <p className="hx-src">{h.source}</p>
            {may && !deciding
              ? <p className="hx-decided-phone"><ActionButton type="button" className="hx-link" onClick={() => { setDeciding(true); setWrong(null); }}>Decided</ActionButton></p>
              : null}
            <div className="hx-held">
              {held.map((row) => (
                <ActionButton reading type="button" key={row.id} onClick={() => goTo(row.id)}>
                  <span className="k">{row.keenness ?? '–'}</span><span>{row.accountName ?? ''}</span><span className="what">{row.product ?? row.title}</span>
                </ActionButton>
              ))}
            </div>
          </div>
        )
        : null}
    </div>
  );
}

/** What the page says after a decision is recorded, with its Undo while the page keeps it (D62). */
function DecidedNote({ d, store, onUndone, onClose }: { d: HoldDecided; store: string; onUndone: () => void; onClose: () => void }) {
  const [state, setState] = useState<'recorded' | 'busy' | 'undone'>('recorded');
  const [wrong, setWrong] = useState<string | null>(null);
  const undo = () => {
    setState('busy'); setWrong(null);
    dealsApi.undoDecideHold(store, d.holdId, d.actionId)
      .then(() => { setState('undone'); onUndone(); })
      .catch((e: unknown) => { setState('recorded'); setWrong(e instanceof DealsError ? `Nothing was put back: ${e.message}.` : 'Nothing was put back. Try again.'); });
  };
  return (
    <p className={wrong ? 'dwn-note warn hx-decided-note' : 'dwn-note ok hx-decided-note'} role="status">
      {state === 'undone'
        ? <>Put back: {d.title} is waiting on us again.</>
        : wrong ?? <>Recorded: {d.title}. Decided: &ldquo;{d.words}&rdquo;. It has left Waiting on us.</>}
      <span className="dwn-acts">
        {state !== 'undone' ? <ActionButton type="button" className="hx-link" disabled={state === 'busy'} onClick={undo}>Undo</ActionButton> : null}
        <ActionButton reading type="button" className="hx-link" disabled={state === 'busy'} onClick={onClose}>Close</ActionButton>
      </span>
    </p>
  );
}

function PromiseRow({ p, goTo }: { p: PromiseLine; goTo: (dealId: string) => void }) {
  const due = dueWords(p);
  return (
    <div className="hx-wrow">
      <div className="hx-line hx-wl nr" onClick={() => goTo(p.dealId)}>
        <span>
          <ActionButton reading type="button" className="t" onClick={(e) => { e.stopPropagation(); goTo(p.dealId); }}>{[p.accountName, p.product].filter(Boolean).join(': ') || 'A deal'}</ActionButton>
          <span className="s"><span>{p.statement}</span><span className="who2">{waitingWords(p)}</span></span>
        </span>
        {due.words ? <span className={due.late ? 'hx-due late' : 'hx-due'}>{due.words}</span> : <span />}
        <Chev />
      </div>
    </div>
  );
}

/**
 * A row's "Keep it" and "Archive it" (BUGS.md D54; Mike, by pop-up, 8 Oct 2026 about 12:37 CEST: "two buttons per row,
 * 'Keep it' (back on Deals) and 'Archive it', each with Undo for a week"). A sorted row stays drawn as what was done, with
 * its Undo, until the list is read again; the deal's history keeps the Undo for the week (live.ts DEAL_ACT_KINDS).
 */
function SortRow({ d, store, onChanged }: { d: ToSort; store: string; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<SortedByHand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const said = (e: unknown, what: string) => setError(e instanceof DealsError ? `${what}: ${e.message}.` : `${what}. Try again.`);
  const sort = (how: 'keep' | 'archive') => {
    setBusy(true); setError(null);
    dealsApi.sort(store, d.id, how)
      .then((r) => { setBusy(false); setDone(r); onChanged(); })
      .catch((e: unknown) => { setBusy(false); said(e, 'Nothing changed'); });
  };
  const undo = () => {
    if (!done) return;
    setBusy(true); setError(null);
    dealsApi.undoSort(store, d.id, done.actionId)
      .then(() => { setBusy(false); setDone(null); onChanged(); })
      .catch((e: unknown) => { setBusy(false); said(e, 'Not undone'); });
  };
  return (
    <span className="hx-tsa">
      {done
        ? <><span className="m">{done.how === 'keep' ? 'Kept: it is back on Deals.' : 'Archived.'}</span>{' '}
          <ActionButton type="button" className="hx-link" disabled={busy} onClick={undo}>{`Undo until ${dayMonth(new Date(done.undoUntil))}`}</ActionButton></>
        : <>
          <ActionButton type="button" className="hx-link" disabled={busy} onClick={() => sort('keep')}>Keep it</ActionButton>
          <ActionButton type="button" className="hx-link m" disabled={busy} onClick={() => sort('archive')}>Archive it</ActionButton>
        </>}
      {error ? <span className="lv-err" role="alert">{error}</span> : null}
    </span>
  );
}

function ToSortList({ store, open, mayEdit, onChanged }: { store: string; open: (p: string) => void; mayEdit: boolean; onChanged: () => void }) {
  const [loaded, reload] = useLoad(() => dealsApi.toSort(store), [store]);
  if (loaded.kind === 'loading') return <Loading what="the deals put aside" />;
  if (loaded.kind === 'error') {
    return <p className="hx-gist">They did not load. Nothing was changed. <ActionButton reading type="button" className="hx-link" onClick={reload}>Try again</ActionButton></p>;
  }
  const deals: ToSort[] = loaded.data.deals;
  if (deals.length === 0) return <p className="hx-gist">Nothing is put aside to be sorted.</p>;
  return (
    <div className="hx-ts">
      {deals.map((d) => (
        <div className="hx-tsr" key={d.id}>
          <span className="t">{d.title}</span>
          {/* BUGS.md D97: a company put away is named nowhere, and has no record to open. */}
          {d.organisationName !== null ? <ActionButton reading type="button" className="hx-link" onClick={() => open(`/companies/${encodeURIComponent(d.organisationId)}`)}>{d.organisationName}</ActionButton> : <span />}
          <span className="m">{[d.stageName, d.valueAmount ? moneyFull(d.valueAmount, d.valueCurrency) : null, d.expectedCloseOn ? `close ${dayMonth(new Date(d.expectedCloseOn), 'utc')}` : null].filter(Boolean).join(', ')}</span>
          <span className="m">{d.reason}{d.by ? `, ${d.by.mine ? 'you' : firstName(d.by)}, ${dayMonth(new Date(d.since))}` : ''}</span>
          {mayEdit ? <SortRow d={d} store={store} onChanged={onChanged} /> : null}
        </div>
      ))}
    </div>
  );
}


const KEYS = ['q', 'product', 'owner', 'keenness', 'stage', 'open'] as const;

/** Lines drawn before "Show more", and how many more each press draws (the brief, section 3: "after the first 50"). */
export const LINES_FIRST = 50;

/** A "By x" chip with a select inside it, in the records pages' grammar (records.css `.tg`): its options carry a count. */
function Narrow({ label, value, options, onChange }: { label: string; value: string; options: FilterOption[]; onChange: (v: string) => void }) {
  return (
    <label className={value ? 'tg on' : 'tg'}>
      <ActionSelect reading aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{label}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label} ({o.n})</option>)}
      </ActionSelect>
    </label>
  );
}

export function HeatPage({ store, open }: PageProps) {
  const [loaded, reload] = useLoad(() => dealsApi.heat(store.tenantId), [store.tenantId]);
  // After an answer on a proposal the page is read again quietly, as the desk did: a reload would blank it to Loading.
  const [fresh, setFresh] = useState<{ tenant: string; data: Heat } | null>(null);
  const kept = useCardNotes();
  // After a write on a card the page is read again; if that moved the line (a keenness re-ranks it), the page follows it
  // so the card and its Undo stay in view.
  const [follow, setFollow] = useState<string | null>(null);
  // How many times the page has been read again: the Added line goes on the next (AddedLine.tsx; pipeline FT1, D30).
  const [reads, setReads] = useState(0);
  const refresh = (after?: { accountId: string; dealId: string }) => {
    void dealsApi.heat(store.tenantId).then((data) => { setFresh({ tenant: store.tenantId, data }); setReads((n) => n + 1); if (after) setFollow(after.dealId); }).catch(() => undefined);
  };
  const state: typeof loaded = loaded.kind === 'ready' && fresh && fresh.tenant === store.tenantId ? { kind: 'ready', data: fresh.data } : loaded;

  // The find and the drop-downs are kept in the address (?q=, ?product=, ?owner=, ?keenness=, ?stage=), so a reload, Back
  // or a link sent to a colleague draws the same list, read through their own rails (U228). Mine is ?owner=<the reader>.
  const [p, set] = useFilterParams(KEYS);
  const q = p.q;
  const setQ = (v: string) => set({ q: v });
  const family = p.product || null;
  const setFamily = (f: string | null) => set({ product: f ?? '' });
  const [found, setFound] = useState<(Found & { q: string }) | null>(null);
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  // Which opportunity's card an opened line shows: its own until a press on "<Account>'s products" switches it.
  const [cardOf, setCardOf] = useState<Readonly<Record<string, string>>>({});
  const [limit, setLimit] = useState(LINES_FIRST);
  const [allUs, setAllUs] = useState(false);
  // The decision just recorded on Waiting on us, kept for its Undo while the page is open (REQ-2026-10-07-171).
  const [decided, setDecided] = useState<HoldDecided | null>(null);
  const [allThem, setAllThem] = useState(false);
  const [phoneRings, setPhoneRings] = useState(false);
  const [phoneNarrow, setPhoneNarrow] = useState(false);
  // On a phone the blocks above the list are one row of buttons, each opening its block in place under the row (module 10
  // U264, Mike by pop-up on 4 Oct 2026: "A. One row of buttons"). `door` is the block open; at 760 and wider every block
  // is drawn as before and the row is not.
  const [door, setDoor] = useState<'show' | 'rings' | 'us' | 'them' | 'heard' | null>(null);
  const [heardOpen, setHeardOpen] = useState(false);
  const [heardSaid, setHeardSaid] = useState<string | null>(null);
  const [asideOpen, setAsideOpen] = useState(false);
  const [jump, setJump] = useState<string | null>(null);
  // Everyone or Mine, kept for the person on the server (U283): Deals opens on what they last pressed. Applied once a
  // visit, and only when the address names no owner of its own (a link sent narrowed keeps its narrowing).
  const [whose, setWhose] = useState<DealsList | null>(null);
  const [whoseSaid, setWhoseSaid] = useState<string | null>(null);

  // The find reads the store as the reader, as on the desk: deals, companies and people, never this page's lines alone.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setFound(null); return; }
    let live = true;
    const t = window.setTimeout(() => {
      dealsApi.find(store.tenantId, term).then((r) => { if (live) setFound(r); }).catch(() => { if (live) setFound(null); });
    }, 200);
    return () => { live = false; window.clearTimeout(t); };
  }, [q, store.tenantId]);

  // A line asked for from elsewhere on the page is scrolled to once it is drawn open.
  useEffect(() => {
    if (!jump) return;
    const el = document.getElementById(`hx-a-${jump}`);
    if (el) { el.scrollIntoView?.({ block: 'start', behavior: 'smooth' }); setJump(null); }
  });

  const lines = state.kind === 'ready' ? state.data.lines : null;
  const me = state.kind === 'ready' ? state.data.me : null;
  const posOf = useMemo(() => new Map((lines ?? []).map((r, i) => [r.id, i])), [lines]);

  // The reader's own Everyone or Mine, once per visit.
  useEffect(() => {
    if (state.kind !== 'ready' || whose !== null) return;
    setWhose(state.data.dealsList);
    if (state.data.dealsList === 'mine' && !p.owner && !p.open) set({ owner: state.data.me });
  }, [state, whose, p.owner, p.open, set]);

  // A line a write on its card moved is kept in view, scrolled only as far as it takes, the list drawn far enough to hold
  // it. A card switched to another of the account's products keeps the line it is opened under.
  useEffect(() => {
    if (!follow || !lines) return;
    const host = Object.entries(cardOf).find(([line, card]) => card === follow && opened.has(line))?.[0] ?? follow;
    const at = posOf.get(host);
    if (at !== undefined && at >= limit) { setLimit(at + 1); return; }
    document.getElementById(`hx-a-${host}`)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    setFollow(null);
  }, [follow, lines, cardOf, opened, posOf, limit]);

  // ?open=<opportunity or account> (the company record's "See it on Deals" and "See them on Deals"): the line is opened
  // and scrolled to once the list is read; an account opens its first line, its keenest.
  const openParam = p.open;
  useEffect(() => {
    if (!openParam || !lines) return;
    const id = posOf.has(openParam) ? openParam : lines.find((r) => r.accountId === openParam)?.id;
    set({ open: '' });
    if (!id) return;
    const at = posOf.get(id)!;
    if (at >= limit) setLimit(at + 1);
    setOpened((x) => new Set(x).add(id));
    setJump(id);
  }, [openParam, lines, posOf, limit, set]);

  if (state.kind === 'loading') return <div className="deals hx"><Loading what="the deals" /></div>;
  if (state.kind === 'error') {
    return (
      <div className="deals hx">
        <div className="dl-says">
          <div className="dl-err">Deals did not load. Nothing was changed.</div>
          <div className="dl-opts"><ActionButton reading type="button" className="dl-opt" onClick={reload}>Try again</ActionButton></div>
        </div>
      </div>
    );
  }
  const heat = state.data;
  const home = heat.homeCurrency;
  const tenant = store.tenantId;
  const all = heat.lines;

  const ring = family ? heat.rings.find((r) => r.family === family) ?? null : null;
  const inRing = ring ? new Set(ring.dealIds) : null;
  const term = q.trim();
  const f: HeatFilter = { product: p.product, owner: p.owner, keenness: p.keenness, stage: p.stage };
  // A line whose card says something (Saved and its Undo) stays on the list whatever narrows it, until it is closed.
  const noted = (r: HeatRow) => Boolean(kept.notes[r.id]) || Boolean(cardOf[r.id] && kept.notes[cardOf[r.id]!]);
  const shown = (r: HeatRow) => noted(r) || (lineInFilter(r, f, inRing) && (term.length < 2 || lineMatches(r, term)));
  const narrowed = Boolean(p.product || p.owner || p.keenness || p.stage) || term.length >= 2;
  const options = filterOptions(all, heat.rings, heat.stages, f);
  const clearAll = { q: '', product: '', owner: '', keenness: '', stage: '' };
  const mine = p.owner !== '' && p.owner === heat.me;

  // What the top of the page adds up (U280): the waiting lines by every filter, the rings by every filter but the
  // product, so the other rings still say what pressing them gives (Mike, by pop-up on 5 Oct 2026). Never the find.
  const inView = new Map(all.filter((r) => lineInFilter(r, f, inRing)).map((r) => [r.id, r]));
  const forRings = all.filter((r) => lineInFilter(r, f, inRing, 'product'));
  const rings = ringsInView(heat.rings, forRings, home);
  const figures = moneyOf(forRings.filter((r) => !r.suppliesUs), home);
  // Whose the rings are: the reader's own under Mine even when they hold nothing (no line names them then).
  const ownerNamed = p.owner && p.owner !== 'none'
    ? all.find((r) => r.holder?.principalId === p.owner)?.holder ?? (p.owner === me ? { principalId: me, name: null, mine: true } : null)
    : null;
  const whoseLine = whoseRingWords(p.owner, ownerNamed, figures);
  const waitingOnUs = heat.waitingOnUs.flatMap((h) => holdInView(h, inView, home) ?? []);
  const waitingOnThem = heat.waitingOnThem.filter((x) => promiseInView(x, inView));

  // Closing a line ends what its card said, as a card's own note did before the page kept them.
  const toggle = (id: string) => {
    if (opened.has(id)) { kept.setNote(id, null); if (cardOf[id]) kept.setNote(cardOf[id]!, null); }
    setOpened((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const goTo = (dealId: string) => {
    const row = all.find((r) => r.id === dealId);
    if (!row) return;
    if (!shown(row)) set(clearAll);
    const at = posOf.get(dealId) ?? 0;
    if (at >= limit) setLimit(at + 1);
    setOpened((s) => new Set(s).add(dealId));
    setJump(dealId);
  };
  // Everyone or Mine, one press (U283): the list narrows at once and the choice is kept for next time.
  const pick = (v: DealsList) => {
    set({ owner: v === 'mine' ? heat.me : '' });
    setWhose(v);
    setWhoseSaid(null);
    if (!currentViewing()) dealsApi.setDealsList(tenant, v).catch(() => setWhoseSaid(v === 'mine' ? 'Mine is shown, but it was not kept for next time.' : 'Everyone is shown, but it was not kept for next time.'));
  };

  const anyNone = rings.some((r) => Number(r.byKeenness.none) > 0);
  // The phone's one line (the brief, section 5 item 3): "£575k from 4 of 13 opportunities with a figure".
  const ringSum = figures.withFigure === 0
    ? 'No figures yet'
    : `${moneyShort(figures.home, home)} from ${figures.withFigure} of ${figures.of} opportunities with a figure`;

  const us = allUs ? waitingOnUs : waitingOnUs.slice(0, 1);
  const them = allThem ? waitingOnThem : waitingOnThem.slice(0, 1);
  const moreUs = waitingOnUs.length - 1;
  const moreThem = waitingOnThem.length - 1;
  const totalHeard = heat.proposals.yours + heat.proposals.colleagues + heat.proposals.anyone;
  const heardMine = heat.heard.some((h) => h.mine);
  const shut = (d: NonNullable<typeof door>) => (door === d ? '' : ' phone-shut');
  const doorPress = (d: NonNullable<typeof door>) => () => { setDoor(door === d ? null : d); if (d === 'rings' && door !== d) setPhoneRings(true); };

  const listed = all.filter(shown);
  const drawn = listed.slice(0, limit);
  const ctx: LiveContext = liveContextOf(heat, tenant, 'deals', open, refresh, kept);
  const products = (r: HeatRow) => all.filter((x) => x.accountId === r.accountId);
  const rankOf = (id: string): LineRank | undefined => {
    const at = posOf.get(id);
    if (at === undefined) return undefined;
    const k = all[at]!.keenness;
    return { pos: at + 1, total: all.length, same: all.filter((x) => x.keenness === k).length };
  };
  const line = (r: HeatRow) => {
    const isOpen = opened.has(r.id);
    const body = `hx-b-${r.id}`;
    return (
      <div className={isOpen ? 'lv-acard open' : 'lv-acard'} id={`hx-a-${r.id}`} key={r.id}>
        <DealsLine row={r} ctx={ctx} isOpen={isOpen} toggle={() => toggle(r.id)} bodyId={body} />
        {isOpen
          ? (
            <OpenedLine row={r} products={products(r)} cardId={cardOf[r.id] ?? r.id} setCard={(id) => setCardOf((c) => ({ ...c, [r.id]: id }))}
              ctx={ctx} rankOf={rankOf} bodyId={body}
              add={heat.mayEdit
                ? (
                  // "Add a product" opens Add deal with the account set (the Deal work lane's W6, U286).
                  <p className="dw-addlink"><ActionButton type="button" className="hx-link" onClick={() => open(`/deals/new?company=${encodeURIComponent(r.accountId)}`)}>Add a product</ActionButton></p>
                )
                : null} />
          )
          : null}
      </div>
    );
  };
  const foundDeals = found && found.q === term ? found : null;

  return (
    <div className="deals hx">
      <div className="dl-top">
        <h1 className="dl-title">Deals</h1>
        <div className="dl-find">
          <ActionInput reading
            type="search" value={q} placeholder="Find a deal, a company or a person"
            aria-label="Find a deal, a company or a person"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setQ(''); }}
          />
        </div>
        {/* "Add deal" opens a page of its own (the Deal work lane's W6, U285, U286), with Add notes (U209, track `notes`) and
            Your notes after it as text links; back from an add, the line says what was added, with its Undo. */}
        <div className="nt-head-deals dw-head-acts">
          {heat.mayEdit ? <ActionButton type="button" className="dw-ink" onClick={() => open('/deals/new')}>Add deal</ActionButton> : null}
          <AddNotes store={store.tenantId} beside={<ActionButton reading type="button" className="nt-back" onClick={() => open('/deals/notes')}>Your notes</ActionButton>} />
        </div>
        {heat.mayEdit ? <AddedLine store={tenant} read={reads} onUndone={() => refresh()} /> : null}
      </div>
      {term.length >= 2
        ? (
          <div className="dl-found" role="region" aria-label="Found">
            {!foundDeals
              ? <p className="dl-says gist">Looking…</p>
              : foundDeals.deals.length + foundDeals.companies.length + foundDeals.people.length === 0
                ? <p className="dl-says gist">Nothing on record matches that.</p>
                : (
                  <>
                    {foundDeals.deals.length > 0 ? <p className="h">Deals</p> : null}
                    {foundDeals.deals.map((d) => (
                      <ActionButton reading type="button" key={`d${d.id}`} onClick={() => { if (posOf.has(d.id)) goTo(d.id); else open(`/deals/${d.id}`); }}>
                        <span>{d.title}</span>{foundDealWhat(d) ? <span className="what">{foundDealWhat(d)}</span> : null}
                      </ActionButton>
                    ))}
                    {foundDeals.companies.length > 0 ? <p className="h">Companies</p> : null}
                    {foundDeals.companies.map((c) => (
                      <ActionButton reading type="button" key={`c${c.id}`} onClick={() => { setQ(''); open(`/companies/${c.id}`); }}><span>{c.name}</span></ActionButton>
                    ))}
                    {foundDeals.people.length > 0 ? <p className="h">People</p> : null}
                    {foundDeals.people.map((x) => (
                      <ActionButton reading type="button" key={`p${x.id}`} onClick={() => { setQ(''); open(`/people/${x.id}`); }}>
                        <span>{x.name}</span>{x.organisationName ? <span className="what">{x.organisationName}</span> : null}
                      </ActionButton>
                    ))}
                  </>
                )}
          </div>
        )
        : null}

      {/* The phone's row of buttons (U264): what each opens is the block below, unchanged. */}
      <div className="hx-doors" role="group" aria-label="What the page holds above the list">
        {heat.shows.length > 0
          ? <ActionButton reading type="button" className="opt" aria-expanded={door === 'show'} onClick={doorPress('show')}>{heat.shows[0]!.name}</ActionButton>
          : null}
        {rings.length > 0
          ? (
            <ActionButton reading type="button" className="opt" aria-expanded={door === 'rings'} onClick={doorPress('rings')}>
              {figures.withFigure > 0 ? <><b>{moneyShort(figures.home, home)}</b> potential</> : 'Revenue potential'}
            </ActionButton>
          )
          : null}
        <ActionButton reading type="button" className="opt d-us" aria-expanded={door === 'us'} onClick={doorPress('us')}>Waiting on us <b>{waitingOnUs.length}</b></ActionButton>
        <ActionButton reading type="button" className="opt" aria-expanded={door === 'them'} onClick={doorPress('them')}>Waiting on them <b>{waitingOnThem.length}</b></ActionButton>
        {totalHeard > 0 || heardSaid
          ? <ActionButton reading type="button" className="opt" aria-expanded={door === 'heard'} onClick={doorPress('heard')}>Heard <b>{totalHeard}</b></ActionButton>
          : null}
        <ActionButton reading type="button" className="opt lv-narrow-one" aria-expanded={phoneNarrow} onClick={() => setPhoneNarrow(!phoneNarrow)}>{phoneNarrow ? 'Close the filters' : 'Narrow the list'}</ActionButton>
      </div>

      {/* The show line while a show runs or its follow-ups are open (U158); a show still ahead keeps its strip. */}
      {heat.shows.length > 0
        ? heat.shows.map((s) => (
          <p className={`hx-show${shut('show')}`} key={s.id}>
            <span className="sec">{s.name}, {showWhen(s, heat.now)}:</span>
            {/* The figure that needs us keeps the display face; the show's context steps back (P10, section 4b). */}
            <span className="ctx"><b>{s.opportunities}</b>{s.opportunities === 1 ? 'opportunity' : 'opportunities'} across <b>{s.accounts}</b>{s.accounts === 1 ? 'account' : 'accounts'}</span>
            {/* BUGS.md D139: the count is of the meetings with no debrief, and says so (Mike, by pop-up, 8 Oct about 12:37 CEST:
                "say '35 meetings with no debrief' instead of 'no notes yet'"). */}
            {s.meetingsWithoutNotes > 0 ? <span className="hide-phone act"><b>{s.meetingsWithoutNotes}</b>{s.meetingsWithoutNotes === 1 ? 'meeting' : 'meetings'} with no debrief</span> : null}
            {s.reworded > 0 ? <span className="ctx"><b>{s.reworded}</b>negative {s.reworded === 1 ? 'note' : 'notes'} reworded</span> : null}
            <ActionButton reading type="button" className="hx-link agenda" onClick={() => open(`/deals/shows/${encodeURIComponent(s.id)}`)}>Agenda, notes and follow-ups</ActionButton>
          </p>
        ))
        : <ShowStrip store={tenant} open={open} />}

      {rings.length > 0
        ? (
          <section className={`hx-rings${shut('rings')}`} aria-label="Revenue potential by product">
            <div className="hx-rh">
              <h2>Revenue potential by product</h2>
              <span className="hx-legend">
                {([5, 4, 3, 2, 1] as const).map((n) => <span key={n}><i className={`hx-sw k${n}`} />{n}</span>)}
                {anyNone ? <span><i className="hx-sw none" />none yet</span> : null}
                <span>keenness</span>
              </span>
            </div>
            <div className="lv-ringsum">
              <span>{figures.withFigure > 0 ? <><b>{moneyShort(figures.home, home)}</b>{ringSum.slice(moneyShort(figures.home, home).length)}</> : ringSum}</span>
              <ActionButton reading type="button" className="hx-link" aria-expanded={phoneRings} onClick={() => setPhoneRings(!phoneRings)}>{phoneRings ? 'Hide the products' : 'By product'}</ActionButton>
            </div>
            <div className={phoneRings ? 'lv-ringrow show' : 'lv-ringrow'}>
              {rings.map((r) => (
                <ActionButton reading type="button" key={r.family} className={`lv-ring${family === r.family ? ' on' : ''}${r.withFigure > 0 ? '' : ' empty'}`} aria-pressed={family === r.family}
                  onClick={() => setFamily(family === r.family ? null : r.family)}>
                  <span className="rw"><Ring k={r.byKeenness} size={44} width={7} /></span>
                  <span className="rn">{r.family}</span>
                  <span className={r.withFigure > 0 ? 'rx' : 'rx none'}>{r.withFigure > 0 ? moneyShort(r.home, home) : 'None yet'}</span>
                  <span className="rc">{withFigureWords(r)}{otherWords(r) ? `, and ${otherWords(r)}` : ''}</span>
                </ActionButton>
              ))}
            </div>
            <p className="hx-rfoot">
              <b className="lv-whose-line">{whoseLine}</b>, a year each
              {heat.figures.yoursWithout > 0 ? `; ${heat.figures.yoursWithout} of yours have none` : ''}.
              {ring ? <> Showing {ring.family}. <ActionButton reading type="button" className="hx-link" onClick={() => setFamily(null)}>Show every product</ActionButton></> : ' Press a ring to filter the list.'}
            </p>
          </section>
        )
        : null}

      <section className={`hx-wlines${door === 'us' || door === 'them' ? '' : ' phone-shut'}`} aria-label="What deals are waiting on">
        <div className={`hx-wgroup g-us${shut('us')}`}>
          <div className="hx-gh">
            <h2>Waiting on us</h2><span className="hx-cnt">{waitingOnUs.length}</span><span className="hx-meta">decisions holding keen opportunities</span>
            {moreUs > 0 ? <ActionButton reading type="button" className="hx-link hx-ghmore" aria-expanded={allUs} onClick={() => setAllUs(!allUs)}>{allUs ? 'Show the first' : 'Show all'}</ActionButton> : null}
          </div>
          {decided ? <DecidedNote key={decided.actionId} d={decided} store={tenant} onUndone={() => refresh()} onClose={() => setDecided(null)} /> : null}
          {waitingOnUs.length === 0
            ? <p className="hx-gist">{narrowed ? 'No keen opportunity in this list is waiting on a decision inside iGame.' : 'No keen client is waiting on a decision inside iGame.'}</p>
            : us.map((h) => (
              <HoldRow key={h.holdId} h={h} home={home} goTo={goTo} store={tenant} may={heat.mayEdit}
                onDecided={(d) => { setDecided(d); refresh(); }}
                held={h.dealIds.flatMap((id) => inView.get(id) ?? [])
                  .sort((a, b) => (b.keenness ?? 0) - (a.keenness ?? 0) || (a.accountName ?? '').localeCompare(b.accountName ?? ''))} />
            ))}
          {moreUs > 0
            ? (
              <div className="hx-line hx-more">
                <span>{allUs ? `${waitingOnUs.length} decisions` : `${moreUs} more: ${waitingOnUs.slice(1, 4).map((h) => h.title).join('; ')}${moreUs > 3 ? '; and others' : ''}`}</span>
                <ActionButton reading type="button" className="hx-link" aria-expanded={allUs} onClick={() => setAllUs(!allUs)}>{allUs ? 'Show the first' : moreUs === 1 ? 'Show it' : 'Show them'}</ActionButton>
              </div>
            )
            : null}
        </div>
        <div className={`hx-wgroup g-them${shut('them')}`}>
          <div className="hx-gh">
            <h2>Waiting on them</h2><span className="hx-cnt">{waitingOnThem.length}</span><span className="hx-meta">what clients said they would do</span>
            {moreThem > 0 ? <ActionButton reading type="button" className="hx-link hx-ghmore" aria-expanded={allThem} onClick={() => setAllThem(!allThem)}>{allThem ? 'Show the first' : 'Show all'}</ActionButton> : null}
          </div>
          {waitingOnThem.length === 0
            ? <p className="hx-gist">{narrowed ? 'Nothing a client promised on this list is outstanding.' : 'Nothing a client or platform promised is outstanding.'}</p>
            : them.map((x) => <PromiseRow key={x.id} p={x} goTo={goTo} />)}
          {moreThem > 0
            ? (
              <div className="hx-line hx-more">
                <span>{allThem ? `${waitingOnThem.length} promises` : `${moreThem} more: ${[...new Set(waitingOnThem.slice(1).map((x) => x.accountName ?? x.product ?? 'a deal'))].slice(0, 3).join(', ')}${new Set(waitingOnThem.slice(1).map((x) => x.accountName ?? x.product ?? 'a deal')).size > 3 ? ' and others' : ''}`}</span>
                <ActionButton reading type="button" className="hx-link" aria-expanded={allThem} onClick={() => setAllThem(!allThem)}>{allThem ? 'Show the first' : moreThem === 1 ? 'Show it' : 'Show them'}</ActionButton>
              </div>
            )
            : null}
        </div>
      </section>

      {totalHeard > 0 || heardSaid
        ? (
          <>
            <div className={`${heat.heard.length > 0 ? 'hx-heard can' : 'hx-heard'}${shut('heard')}`} onClick={heat.heard.length > 0 ? () => setHeardOpen(!heardOpen) : undefined}>
              <span className="sec">Cobrain heard <b>{totalHeard}</b>new {totalHeard === 1 ? 'opportunity' : 'opportunities'}</span>
              <span className="hlsub">{heat.proposals.since ? `since ${dayMonth(new Date(heat.proposals.since))}: ` : ''}<span className="yours-n"><b>{heat.proposals.yours}</b>yours to accept</span>, <span className="col-n"><b>{heat.proposals.colleagues}</b>with colleagues</span>{heat.proposals.anyone > 0 ? <>, <span className="any-n"><b>{heat.proposals.anyone}</b>for anyone to take</span></> : null}</span>
              {heat.heard.length > 0
                ? <ActionButton reading type="button" className="opt" aria-expanded={heardOpen} onClick={(e) => { e.stopPropagation(); setHeardOpen(!heardOpen); }}>{heardMine ? (heardOpen ? 'Close yours' : 'Go through yours') : (heardOpen ? 'Close them' : 'Go through them')}</ActionButton>
                : null}
              {heat.heard.length > 0 ? <span className={heardOpen ? 'hx-hlchev open' : 'hx-hlchev'}><Chev /></span> : null}
            </div>
            {heardSaid ? <p className="dl-said" role="status">{heardSaid}</p> : null}
            {heardOpen
              ? heat.heard.map((h) => <HeardCard key={h.id} h={h} store={tenant} me={heat.me} team={heat.team ?? []} open={open} onAnswered={(w) => { setHeardSaid(w); refresh(); }} />)
              : null}
          </>
        )
        : null}

      <div className={`${narrowed ? 'recs hx-filters narrowed' : 'recs hx-filters'}${phoneNarrow ? ' phone-open' : ''}`}>
        {/* Everyone or Mine at the front of the filter bar, one press (U283). */}
        <span className="lv-whose" role="group" aria-label="Whose opportunities">
          <ActionButton reading type="button" className="opt" aria-pressed={!p.owner} onClick={() => pick('everyone')}>Everyone</ActionButton>
          <ActionButton reading type="button" className="opt" aria-pressed={mine} onClick={() => pick('mine')}>Mine</ActionButton>
        </span>
        <div className="chips">
          <Narrow label="By product" value={p.product} options={options.product} onChange={(v) => set({ product: v })} />
          <Narrow label="By owner" value={p.owner} options={options.owner} onChange={(v) => set({ owner: v })} />
          <Narrow label="By keenness" value={p.keenness} options={options.keenness} onChange={(v) => set({ keenness: v })} />
          <Narrow label="By stage" value={p.stage} options={options.stage} onChange={(v) => set({ stage: v })} />
          {narrowed ? <ActionButton reading type="button" className="hx-link" onClick={() => set(clearAll)}>Clear all</ActionButton> : null}
        </div>
        <span className="hx-count">{narrowed ? <><b>{listed.length}</b> of {all.length} opportunities</> : <><b>{all.length}</b> {all.length === 1 ? 'opportunity' : 'opportunities'}</>}</span>
        {whoseSaid ? <p className="lv-said" role="status">{whoseSaid}</p> : null}
      </div>

      <section className="hx-list" aria-label="Opportunities by keenness">
        <div className="lv-colhead lv-ohead"><span /><span>Account and product</span><span>Owner</span><span>Stage</span><span className="ss">Status</span><span className="nw">New today</span><span className="r">Revenue potential</span><span /></div>
        {listed.length === 0
          ? (
            <p className="hx-gist">
              {narrowed
                ? <>{mine ? 'You own no open opportunity that matches.' : 'No opportunity matches that.'} <ActionButton reading type="button" className="hx-link" onClick={() => set(clearAll)}>Clear all</ActionButton></>
                : 'No opportunity is open.'}
            </p>
          )
          : drawn.map(line)}
        {listed.length > drawn.length
          ? (
            <div className="hx-line hx-more lv-more">
              <span>{drawn.length} of {listed.length} shown</span>
              <ActionButton reading type="button" className="hx-link" onClick={() => setLimit(limit + LINES_FIRST)}>Show more</ActionButton>
            </div>
          )
          : null}

        {/* A parked deal is a line of the list, marked "Parked till <date>: <why>" (pipeline FT1, Mike by pop-up on 5 Oct
            2026: "Everywhere, ring included", and "Remove the fold"). The foot below is the whole store's: drawn while
            nothing narrows the list, since none of it is a line of the list. */}
        {/* Open, the list stays while its last row is sorted, so that row's Undo is not lost with the line (D54). */}
        {(heat.setAside.toSort > 0 || heat.setAside.archived > 0 || asideOpen) && !narrowed
          ? (
            <>
              <p className="hx-archive">
                <span>
                  The old HubSpot deals:{' '}
                  {[heat.setAside.toSort > 0 ? `${heat.setAside.toSort} put aside to be sorted by hand${heat.setAside.toSortSince ? `, since ${dayMonth(new Date(heat.setAside.toSortSince))}` : ''}` : null,
                    heat.setAside.archived > 0 ? `${heat.setAside.archived} archived` : null].filter(Boolean).join('; ')}
                </span>
                {heat.setAside.toSort > 0
                  ? <ActionButton reading type="button" className="hx-link" aria-expanded={asideOpen} onClick={() => setAsideOpen(!asideOpen)}>{asideOpen ? 'Hide them' : 'Show them'}</ActionButton>
                  : null}
              </p>
              {asideOpen ? <ToSortList store={tenant} open={open} mayEdit={heat.mayEdit} onChanged={() => refresh()} /> : null}
            </>
          )
          : null}
        <ClosedDeals store={tenant} open={open} stamp={reads} changed={() => refresh()} />
      </section>
      <CloseToasts ctx={ctx} />
    </div>
  );
}
